import type { OrgBranding } from '@emr/shared';
import { createContext, useContext } from 'react';

import type { ResolvedAccentSource } from './accentSource.ts';
import type { Accent } from './palette.ts';
import type { ColorMode, ThemePreference } from './preference.ts';

export interface ThemeContextValue {
  readonly preference: ThemePreference;
  /** The theme actually in effect, with `'system'` already resolved against the OS setting. */
  readonly resolvedMode: 'light' | 'dark';
  /** The practice's own branding, or `null` before it is known (before sign-in, or before
   * `OrgBrandingSync.tsx`'s fetch has completed). See that component for who populates this. */
  readonly orgBranding: OrgBranding | null;
  /** Which accent actually governs right now — see `accentSource.ts`'s own header. Only ever
   * `'org'` when `orgBranding` has a brand colour; `ThemeMenu.tsx` uses this to decide which radio
   * is selected and whether the accent picker is offered at all. */
  readonly resolvedAccentSource: ResolvedAccentSource;
  setColorMode: (mode: ColorMode) => void;
  /** Sets this member's own accent pick **and** switches to it — the explicit "choose my own
   * colour" action `ThemeMenu.tsx`'s accent radio group performs. Refused with no effect while the
   * practice's `allowUserColorPicker` is `false`; `ThemeMenu.tsx` does not offer the picker in that
   * case in the first place, so reaching this with the toggle off would only happen from a stale UI. */
  setAccent: (accent: Accent) => void;
  /** Defers back to the practice's own brand colour — the counterpart to `setAccent`. A no-op,
   * safely, when the practice has no brand colour to defer to. */
  deferToOrgAccent: () => void;
  /** Populates `orgBranding` from the server. Called by `OrgBrandingSync.tsx` once a session
   * exists, never by anything else — this is session-driven state, not a user preference. */
  setOrgBranding: (branding: OrgBranding | null) => void;
  /**
   * A human-readable account of the last theme that was applied, measured, and rolled back —
   * `null` when nothing has been rejected. Rendered visibly rather than logged: `no-console` is a
   * PHI-egress control in this repo, and a silently-discarded theme is exactly the failure the
   * guard exists to make noticeable. Also covers a practice's brand colour specifically failing its
   * own contrast check (`ThemeProvider.tsx`'s `applyAccentAware`) — the app falls back to the
   * closed-enum accent in that case rather than rendering the failing colour, and this message says
   * so.
   */
  readonly rejection: string | null;
  dismissRejection: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Throws rather than returning a default when used outside the provider. A silent default here
 * would mean a control that appears to work and changes nothing.
 */
export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return value;
}
